import { useCallback } from "react";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { Stack, TextField, Box, Button } from "@mui/material";
import { IconSearchSelect } from "@components/admin/icon-selector";
import { kmarIcons as All } from "@config/kmar-icons";
import useFavouritesCollectionStore from "@stores/shared/useFavouritesCollectionStore";
import useFavouritesUserCollectionStore from "@stores/client/useFavouritesUserCollectionStore";
import { useCollectionOperations } from "../hooks/useCollectionOperations";

const initialValues = (item) => ({
  name: item?.name || "",
  icon: item?.icon || "",
});

export const FavouriteCollectionForm = (props) => {
  const { item, onSubmit, isFromFavouritesButton = false } = props;
  const { t } = useTranslation();

  const { collections } = useFavouritesCollectionStore();
  // Get user collections store for favourites button context
  // gets list only of user collections (not a tree and also no system collections)
  const { favouritesUserCollections } = useFavouritesUserCollectionStore();

  const collectionsInUse = isFromFavouritesButton
    ? favouritesUserCollections
    : collections;

  const {
    handleCreateCollection,
    handleUpdateCollection,
    handleCreateUserCollection,
  } = useCollectionOperations(item);

  const validationSchema = Yup.object({
    name: Yup.string()
      .max(255, t("common.maxLength", { count: 255 }))
      .required(
        t("common.isRequired", { item: t("favourites.collectionName") }),
      ),
  });

  const formik = useFormik({
    initialValues: initialValues(item),
    validationSchema,
    enableReinitialize: true,

    onSubmit: useCallback(
      async (values, helpers) => {
        const formData = item
          ? {
              ...item,
              ...values,
              order: item.order,
              status: 4,
            }
          : {
              ...values,
              order: collectionsInUse[collectionsInUse.length - 1].order + 1,
              status: 4,
            };

        const handleSuccess = () => {
          helpers.setStatus({ success: true });
          helpers.setSubmitting(false);
          onSubmit?.();
        };

        const handleError = () => {
          helpers.setStatus({ success: false });
          helpers.setSubmitting(false);
        };

        try {
          if (item) {
            handleUpdateCollection(item.id, formData, handleSuccess);
          } else if (isFromFavouritesButton) {
            handleCreateUserCollection(formData, handleSuccess);
          } else {
            handleCreateCollection(formData, handleSuccess);
          }
        } catch (err) {
          handleError();
          helpers.setErrors({ submit: err.message });
        }
      },
      [
        item,
        handleCreateCollection,
        handleCreateUserCollection,
        handleUpdateCollection,
        onSubmit,
        isFromFavouritesButton,
      ],
    ),
  });

  const buttonLabel = item ? t("apps.appUpdate") : t("apps.appCreate");

  return (
    <Box sx={{ p: 3, flex: 1, overflow: "auto" }}>
      <form onSubmit={formik.handleSubmit}>
        <Stack spacing={1}>
          <TextField
            error={!!(formik.touched.name && formik.errors.name)}
            fullWidth
            helperText={formik.touched.name && formik.errors.name}
            label={t("favourites.collectionName")}
            name="name"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            required
            value={formik.values.name}
            slotProps={{ htmlInput: { maxLength: 255 } }}
          />
          <IconSearchSelect
            key={item?.id}
            value={formik.values.icon}
            onChange={(value) => formik.setFieldValue("icon", value)}
            customIcons={All}
          />

          <Button
            loading={formik.isSubmitting}
            type="submit"
            variant="contained"
          >
            {buttonLabel}
          </Button>
        </Stack>
      </form>
    </Box>
  );
};

FavouriteCollectionForm.propTypes = {
  item: PropTypes.object,
  onSubmit: PropTypes.func,
  isFromFavouritesButton: PropTypes.bool,
};
